/**
 * @features/dashboard/components/mobile/MobileDashboardHeader.jsx
 * --------------------------------------------------------------
 * Sticky mobile-only top bar that sits above DashboardLayout's content.
 * Hidden on ≥md viewports where the desktop sidebar provides navigation
 * context.
 *
 * Contents:
 *   • Left:  hamburger button → opens MobileMoreSheet
 *   • Mid:   page title (auto-truncates) + optional subtitle
 *   • Right: page-specific action slot (passed via DashboardLayout's
 *            `mobileHeaderActions` prop — e.g. notifications bell,
 *            Add buttons that should always be reachable from the
 *            current page).
 *
 * Safe-area: top padding inherits `env(safe-area-inset-top)` so notches
 * + dynamic islands don't overlap the title.
 *
 * v2.71.38 — Initial.
 */

import React from "react";
import { Menu } from "lucide-react";

export function MobileDashboardHeader({
  title,
  subtitle,
  onMenuOpen,
  rightActions,
}) {
  return (
    <header
      className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E4E4E7]"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid="mobile-dashboard-header"
    >
      <div className="flex items-center gap-2 px-3 h-14">
        <button
          type="button"
          onClick={onMenuOpen}
          aria-label="Open dashboard menu"
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-sm text-[#27272A] hover:bg-[#F4F4F5] active:bg-[#E4E4E7]"
          data-testid="mobile-header-menu-btn"
        >
          <Menu size={20} />
        </button>
        <div className="min-w-0 flex-1">
          {title && (
            <div
              className="font-heading font-semibold text-[15px] leading-tight text-[#09090B] truncate"
              data-testid="mobile-header-title"
            >
              {title}
            </div>
          )}
          {subtitle && (
            <div className="text-[11px] text-[#71717A] leading-tight truncate">
              {subtitle}
            </div>
          )}
        </div>
        {rightActions && (
          <div
            className="flex items-center gap-1 flex-shrink-0"
            data-testid="mobile-header-actions"
          >
            {rightActions}
          </div>
        )}
      </div>
    </header>
  );
}

export default MobileDashboardHeader;
